import { useEffect, useRef, useState } from 'react';
import { FaPaperPlane, FaSpinner, FaWhatsapp, FaXmark } from 'react-icons/fa6';
import { getFloatingWhatsappHref } from '../../lib/floatingWhatsapp';
import { submitWhatsappLead } from '../../lib/whatsappLead';
import { useGlobal } from '../../context/GlobalContext';
import { getUiText } from '../../lib/uiText';
import styles from './WhatsAppButton.module.css';

const COUNTRY_CODES = ['+52', '+1', '+34', '+57', '+54', '+56', '+51', '+58', '+593', '+502', '+503', '+504', '+505', '+506', '+507', '+55'];
const clean = (value) => value.replace(/\D/g, '');
const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const dispatchGtmEventBeforeNavigation = (navigate, source) => {
  let hasNavigated = false;
  let fallbackTimer;
  const navigateOnce = () => {
    if (hasNavigated) return;
    hasNavigated = true;
    window.clearTimeout(fallbackTimer);
    navigate();
  };

  fallbackTimer = window.setTimeout(navigateOnce, 800);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'generate_lead',
    lead_source: source === 'chatbot' ? 'chatbot_whatsapp' : 'sitio_form_whatsapp',
    form: 'contacto',
    eventCallback: navigateOnce,
    eventTimeout: 800,
  });
};

const WhatsAppButton = () => {
  const { locale, companyInfo } = useGlobal();
  const text = (key) => getUiText(locale, key);
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+52');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [retryHref, setRetryHref] = useState('');
  const busyRef = useRef(false);
  const sourceRef = useRef('website');
  const containerRef = useRef(null);
  const destinationHref = getFloatingWhatsappHref(companyInfo);
  const isValid = name.trim().length > 0 && validEmail(email.trim()) && clean(`${countryCode}${phone}`).length >= 10 && clean(`${countryCode}${phone}`).length <= 15;

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!busyRef.current && containerRef.current && !containerRef.current.contains(event.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    const closeForChatbot = () => setIsOpen(false);
    const openForVisit = (event) => {
      if (busyRef.current) return;
      sourceRef.current = event.detail?.source === 'chatbot' ? 'chatbot' : 'website';
      setRetryHref(''); setError(''); setIsOpen(true);
    };
    window.addEventListener('chatbot-open', closeForChatbot);
    window.addEventListener('whatsapp-form-open', openForVisit);
    return () => {
      window.removeEventListener('chatbot-open', closeForChatbot);
      window.removeEventListener('whatsapp-form-open', openForVisit);
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isValid || busyRef.current || !destinationHref) return;
    busyRef.current = true;
    setError('');
    setIsSubmitting(true);
    // Reserve the tab during the click so the browser allows the new window.
    // WhatsApp is loaded only after Strapi confirms that the lead was saved.
    const whatsappWindow = window.open('about:blank', '_blank');
    if (!whatsappWindow) {
      setError(text('whatsappPopupBlocked'));
      busyRef.current = false;
      setIsSubmitting(false);
      return;
    }
    whatsappWindow.opener = null;
    whatsappWindow.document.title = text('sending');
    const loadingText = whatsappWindow.document.createElement('p');
    loadingText.textContent = text('sending');
    whatsappWindow.document.body.append(loadingText);
    const userPhone = `${countryCode}${clean(phone)}`;
    const lead = { name: name.trim(), email: email.trim(), phone: userPhone, message: text(sourceRef.current === 'chatbot' ? 'whatsappChatInterest' : 'whatsappInterest') };
    const source = sourceRef.current;
    try {
      await submitWhatsappLead(lead);
      const url = new URL(destinationHref, window.location.origin);
      url.searchParams.set('text', `${text('whatsappGreeting')} ${lead.name}. ${lead.message}`);
      dispatchGtmEventBeforeNavigation(() => {}, source);
      setName(''); setEmail(''); setPhone('');
      if (whatsappWindow.closed) {
        // A manual retry opens WhatsApp without saving the same lead again.
        setRetryHref(url.toString());
        setError(text('whatsappWindowClosed'));
      } else {
        whatsappWindow.location.replace(url.toString());
        setIsOpen(false);
      }
    } catch {
      whatsappWindow.close();
      setError(text('whatsappSaveError'));
    } finally {
      busyRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (!destinationHref) return null;
  return <div ref={containerRef} className={styles.container}>
    <div className={`${styles.form} ${isOpen ? styles.open : ''}`}>
      <button className={styles.close} disabled={isSubmitting} type="button" onClick={() => setIsOpen(false)} aria-label={text('close')}><FaXmark /></button>
      <div className={styles.heading}><FaWhatsapp /><div><h2>{text('whatsappHeading')}</h2><p>{text('whatsappIntro')}</p></div></div>
      {retryHref ? <div role="alert">
        <p className={styles.error}>{error}</p>
        <a data-whatsapp-continue className={styles.submit} href={retryHref} target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)}>{text('whatsappOpen')}</a>
      </div> : <form onSubmit={handleSubmit} aria-busy={isSubmitting}>
        <label>{text('whatsappFullName')}<input disabled={isSubmitting} value={name} onChange={(event) => setName(event.target.value)} placeholder={text('whatsappNamePlaceholder')} autoComplete="name" maxLength={50} /></label>
        <label>{text('whatsappEmail')}<input disabled={isSubmitting} type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder={text('whatsappEmailPlaceholder')} autoComplete="email" maxLength={100} /></label>
        <label>{text('whatsappPhone')}<span className={styles.phone}><select disabled={isSubmitting} aria-label={text('whatsappCountryCode')} value={countryCode} onChange={(event) => setCountryCode(event.target.value)}>{COUNTRY_CODES.map((code) => <option key={code} value={code}>{code === '+52' ? 'MX +52' : code}</option>)}</select><input disabled={isSubmitting} type="tel" value={phone} onChange={(event) => setPhone(clean(event.target.value))} placeholder="55 1234 5678" autoComplete="tel-national" maxLength={15} /></span></label>
        <button className={styles.submit} type="submit" disabled={!isValid || isSubmitting}>{isSubmitting ? <FaSpinner className={styles.spinner} aria-hidden="true" /> : <FaPaperPlane />}{isSubmitting ? text('sending') : text('whatsappSend')}</button>
        {error && <p className={styles.error} role="alert">{error}</p>}
      </form>}
      <small>🔒 {text('whatsappPrivacy')}</small><footer>powered by <b>koppi</b></footer>
    </div>
    <button className={styles.button} type="button" disabled={isSubmitting} onClick={() => { if (!isOpen) { sourceRef.current = 'website'; setRetryHref(''); setError(''); } setIsOpen((open) => !open); }} aria-expanded={isOpen} aria-label={isOpen ? text('close') : text('whatsappOpen')}>{isOpen ? <FaXmark /> : <FaWhatsapp />}</button>
  </div>;
};

export default WhatsAppButton;
